import faviconMc from '@/assets/favicon-motocontroler.png?no-inline';
import faviconMe from '@/assets/favicon-mobilekspert.png?no-inline';
import logoMc from '@/assets/logo.png';
import logoMe from '@/assets/logo-me.png';
import type { ApiBrandType } from '@/api/types/models.types';

/**
 * Zwraca lokalny favicon marki dla podanego `brandType`.
 *
 * MOBILEKSPERT (ME) ma własną ikonę; pozostałe marki
 * (MOTOCONTROLER, RAPORT_AUTA) oraz brak wartości korzystają z MotoControler.
 */
export const getBrandFavicon = (brandType?: string | null): string =>
  brandType === 'MOBILEKSPERT' ? faviconMe : faviconMc;

/**
 * Zwraca logo marki (brand) dla podanego `brandType`.
 *
 * MOBILEKSPERT (ME) ma własne logo; pozostałe marki
 * (MOTOCONTROLER, RAPORT_AUTA) oraz brak wartości korzystają z domyślnego
 * logo MotoControler.
 */
export const getBrandLogo = (brandType?: string | null): string =>
  brandType === 'MOBILEKSPERT' ? logoMe : logoMc;

/**
 * Dane kontaktowe marki (brand) gotowe do renderu w stopce / komunikatach.
 * Wartości pochodzą wyłącznie z API — gdy backend nie poda pola, zostaje
 * `undefined` i dany element kontaktu nie jest renderowany (bez fallbacku
 * na zaszyte na sztywno dane).
 */
export interface BrandContact {
  phoneDisplay?: string;
  phoneHref?: string;
  email?: string;
  emailHref?: string;
}

/** Buduje link `tel:` z numeru w formie wyświetlanej (usuwa spacje, nawiasy, myślniki). */
const toTelHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

/**
 * Składa dane kontaktowe marki dokładnie z wartości zwróconych przez API.
 * Puste/`null` pola pozostają `undefined` (nic nie pokazujemy zamiast nich).
 */
export const buildBrandContact = (
  phone?: string | null,
  email?: string | null,
): BrandContact => {
  const phoneDisplay = phone?.trim() || undefined;
  const resolvedEmail = email?.trim() || undefined;
  return {
    phoneDisplay,
    phoneHref: phoneDisplay ? toTelHref(phoneDisplay) : undefined,
    email: resolvedEmail,
    emailHref: resolvedEmail ? `mailto:${resolvedEmail}` : undefined,
  };
};

/**
 * Stan pobierania marki linku dostępowego na ekranie PIN (ReportAccess).
 * Po rozwiązaniu niesie też dane kontaktowe brandu (telefon/e-mail z bazy),
 * używane do brandowania stopki.
 */
export type AccessLinkBrandState =
  | { status: 'loading' }
  | {
      status: 'resolved';
      brandType?: ApiBrandType | null;
      contactEmail?: string | null;
      contactPhone?: string | null;
    }
  | { status: 'error' };

/**
 * Wylicza `src` logo w nagłówku ekranu dostępu (PIN) na podstawie stanu
 * pobierania marki:
 *  - `null`      → marka jeszcze się ładuje: nie pokazujemy logo (rezerwujemy
 *                  miejsce), żeby uniknąć mignięcia złym logiem,
 *  - `string`    → marka pobrana: właściwe logo brandu,
 *  - `undefined` → pobranie nie powiodło się: domyślne logo MotoControler.
 */
export const resolveAccessLinkLogoSrc = (
  state: AccessLinkBrandState,
): string | null | undefined => {
  switch (state.status) {
    case 'loading':
      return null;
    case 'resolved':
      return getBrandLogo(state.brandType);
    case 'error':
      return undefined;
  }
};

/**
 * Dane kontaktowe stopki na ekranie dostępu (PIN) — wyłącznie z API. Dopóki
 * marka się nie rozwiąże (loading/error) zwracamy pusty kontakt, więc telefon
 * i e-mail pojawiają się dopiero z danymi z backendu.
 */
export const resolveAccessLinkContact = (
  state: AccessLinkBrandState,
): BrandContact =>
  state.status === 'resolved'
    ? buildBrandContact(state.contactPhone, state.contactEmail)
    : {};
